/**
 * Chromecast integration - Google Cast Web Sender SDK, default media
 * receiver (chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID). Deliberately
 * NOT using shaka.cast.CastProxy: that requires a Shaka-aware *custom*
 * receiver app (its own richer protocol for manifest/ABR handling), which
 * means registering a Cast application ($5, Google Cast SDK Developer
 * Console) and hosting a receiver page. The default receiver needs no
 * registration at all and plays standard HLS/MP4 URLs directly. The
 * Chromecast device fetches the stream itself (independently of the
 * browser, with no cookies/headers of its own), so the media URL carries an
 * auth token as a query param - see withAuthToken() below.
 *
 * Cast.isAvailable() only becomes true after the SDK loads AND finds a Cast
 * extension/device on the network - callers should listen for
 * 'cast-api-available' on window rather than checking synchronously at
 * script-load time.
 */
(function () {
    let apiAvailable = false;

    window['__onGCastApiAvailable'] = function (isAvailable) {
        if (!isAvailable || typeof cast === 'undefined') return;
        try {
            cast.framework.CastContext.getInstance().setOptions({
                receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
                autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
            });
            apiAvailable = true;
            window.dispatchEvent(new Event('cast-api-available'));

            cast.framework.CastContext.getInstance().addEventListener(
                cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
                (event) => {
                    const isCasting = [
                        cast.framework.SessionState.SESSION_STARTED,
                        cast.framework.SessionState.SESSION_RESUMED
                    ].includes(event.sessionState);
                    window.dispatchEvent(new CustomEvent('cast-state-changed', { detail: { isCasting } }));
                }
            );
        } catch (err) {
            console.error('[Cast] Init failed:', err);
        }
    };

    function isAvailable() {
        return apiAvailable;
    }

    function isCasting() {
        if (!apiAvailable) return false;
        return !!cast.framework.CastContext.getInstance().getCurrentSession();
    }

    /**
     * Guess a Cast-compatible MIME type from a stream URL. Only needs to be
     * roughly right - the default receiver uses it to pick a playback path
     * (HLS vs progressive MP4), not for strict validation.
     */
    function guessContentType(url) {
        if (url.includes('.m3u8') || url.includes('m3u8')) return 'application/x-mpegurl';
        if (url.includes('.mp4')) return 'video/mp4';
        if (url.includes('.mkv')) return 'video/x-matroska';
        return 'application/x-mpegurl'; // most of this app's streams end up as HLS
    }

    /**
     * The Chromecast device fetches the media URL directly from its own
     * network context (not through this browser tab), so it can't send the
     * cookie or Authorization header the SPA normally uses - the stream is
     * behind the global /api auth gate like everything else. A query-param
     * token is the only way to reach it, but that means it lands in
     * plaintext in nginx/proxy access logs on every cast - so instead of
     * reusing the SPA's own full-session token (valid 24h, full API access),
     * mint a short-lived (10min) stream-only-scoped one just for this
     * (server: generateCastToken()/apiAuthGate). A leaked cast token is
     * useless for anything but fetching a stream, and only for a few
     * minutes.
     */
    async function withAuthToken(url) {
        let token;
        try {
            const res = await fetch('/api/auth/cast-token', {
                headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` }
            });
            if (res.ok) token = (await res.json()).token;
        } catch (err) {
            console.error('[Cast] Failed to mint cast token:', err);
        }
        if (!token) return url;
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}token=${encodeURIComponent(token)}`;
    }

    /**
     * Start (or join) a Cast session and load media into it. `url` must be
     * absolute and publicly reachable - the Chromecast device fetches it
     * directly, not through this browser tab.
     */
    async function startCasting(url, meta = {}) {
        if (!apiAvailable) throw new Error('Cast API not available');

        const context = cast.framework.CastContext.getInstance();
        let session = context.getCurrentSession();
        if (!session) {
            await context.requestSession();
            session = context.getCurrentSession();
        }
        if (!session) throw new Error('No cast session');

        const mediaInfo = new chrome.cast.media.MediaInfo(await withAuthToken(url), guessContentType(url));
        // Required by the default media receiver - without an explicit
        // streamType (defaults to UNKNOWN) it silently declines the
        // LoadRequest and falls straight back to the idle backdrop without
        // ever fetching the media URL.
        mediaInfo.streamType = meta.isLive
            ? chrome.cast.media.StreamType.LIVE
            : chrome.cast.media.StreamType.BUFFERED;
        mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
        if (meta.title) mediaInfo.metadata.title = meta.title;
        if (meta.subtitle) mediaInfo.metadata.subtitle = meta.subtitle;
        if (meta.poster) mediaInfo.metadata.images = [new chrome.cast.Image(meta.poster)];

        const request = new chrome.cast.media.LoadRequest(mediaInfo);
        return session.loadMedia(request);
    }

    function stopCasting() {
        if (!apiAvailable) return;
        const session = cast.framework.CastContext.getInstance().getCurrentSession();
        if (session) session.endSession(true);
    }

    function getCastDeviceName() {
        if (!apiAvailable) return null;
        const session = cast.framework.CastContext.getInstance().getCurrentSession();
        return session ? session.getCastDevice().friendlyName : null;
    }

    /** Convert a same-origin relative URL to absolute (Cast device needs a full URL). */
    function toAbsoluteUrl(url) {
        if (/^https?:\/\//i.test(url)) return url;
        return window.location.origin + (url.startsWith('/') ? url : '/' + url);
    }

    window.Cast = { isAvailable, isCasting, startCasting, stopCasting, getCastDeviceName, toAbsoluteUrl };
})();
